import { TableContext } from "../context";
import React, { useState, useContext, useEffect, useMemo } from "react";
import "../App.css";
import Papa from "papaparse";
import { DateTime } from 'luxon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LaunchIcon from '@mui/icons-material/Launch';
import {
    McTable,
    McButton,
    McPagination
} from "@maersk-global/mds-react-wrapper";
import FilterButton from "./FilterButton";

/**
 * CICDTable component for displaying and filtering CICD changes data.
 * Provides options to filter data by state, environment, repository, and other fields.
 * Allows downloading the filtered data as a CSV file.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.Header1 - The first header for the table.
 * @param {string} props.Header2 - The second header for the table.
 * @param {string} props.Header3 - The third header for the table.
 * @param {string} props.Header4 - The fourth header for the table.
 * @param {string} props.Header5 - The fifth header for the table.
 * @param {string} props.Header6 - The sixth header for the table.
 * @param {string} props.Header7 - The seventh header for the table.
 * @param {string} props.Header8 - The eighth header for the table.
 */
export default function CICDTable(props) {
    const { cicdTableData, selectedTime, search, searchType } = useContext(TableContext);
    const [headingText, setHeadingText] = useState('Ongoing Changes');
    const [activeFilters, setActiveFilters] = useState([]);
    const [tableStyles, setTableStyles] = useState();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 100; // Number of records per page

    // Table state preservation
    const [sortColumn, setSortColumn] = useState("completedAt");
    const [sortDirection, setSortDirection] = useState("descending");
    const [tableScrollPosition, setTableScrollPosition] = useState(0);

    // Define table columns for the filter system and McTable
    const tableColumns = [
        {
            id: "changeNumber",
            label: props.Header1 || "Change Number",
            sortable: true,
        },
        {
            id: "applicationId",
            label: props.Header2 || "Application ID",
            sortable: true,
        },
        {
            id: "applicationName",
            label: props.Header3 || "Application Name",
            sortable: true,
        },
        {
            id: "repositoryName",
            label: props.Header4 || "Repository Name",
            sortable: true,
        },
        {
            id: "completedAt",
            label: props.Header5 || "Completed At",
            sortable: true,
        },
        {
            id: "environment",
            label: props.Header6 || "Environment",
            sortable: true,
        },
        {
            id: "deploymentLink",
            label: props.Header7 || "Deployment Link",
            sortable: false,
        },
        {
            id: "state",
            label: props.Header8 || "State",
            sortable: true,
        }
    ];

    // Filter columns for the advanced filter (using original field names)
    const filterColumns = [
        { id: "Change Number", label: "Change Number" },
        { id: "Application ID", label: "Application ID" },
        { id: "Application Name", label: "Application Name" },
        { id: "Repository Name", label: "Repository Name" },
        { id: "Environment", label: "Environment" },
        { id: "State", label: "State" },
        { id: "Completed At", label: "Completed At" }
    ];

    const sortArrayByProperty = (data, property, order = 'ascending') => {
        return data.sort((a, b) => {
            let comparison = 0;

            if (property === "completedAt") {
                const dateA = DateTime.fromFormat(a[property], 'dd-MM-yyyy HH:mm:ss');
                const dateB = DateTime.fromFormat(b[property], 'dd-MM-yyyy HH:mm:ss');
                if (dateA < dateB) comparison = -1;
                else if (dateA > dateB) comparison = 1;
            }
            else if (property === "changeNumber") {
                const valA = a[property];
                const valB = b[property];
                const numA = typeof valA === "number" ? valA : parseInt(valA.toString().replace(/\D/g, ""), 10);
                const numB = typeof valB === "number" ? valB : parseInt(valB.toString().replace(/\D/g, ""), 10);

                if (!isNaN(numA) && !isNaN(numB)) {
                    if (numA < numB) comparison = -1;
                    else if (numA > numB) comparison = 1;
                } else {
                    if (String(valA) < String(valB)) comparison = -1;
                    else if (String(valA) > String(valB)) comparison = 1;
                }
            }
            else {
                if (a[property] > b[property]) {
                    comparison = 1;
                } else if (a[property] < b[property]) {
                    comparison = -1;
                }
            }
            return order === 'ascending' ? comparison : comparison * -1;
        });
    }

    // Apply global search filters across all columns
    const applyGlobalSearch = (data, searchTerms) => {
        if (!searchTerms || searchType !== 'global') return data;
        
        // Split comma-separated search terms and clean them
        const terms = searchTerms.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0);
        if (terms.length === 0) return data;
        
        return data.filter(item => {
            // Check if any search term matches any column value
            return terms.some(term => {
                return Object.values(item).some(value => {
                    if (value === null || value === undefined) return false;
                    return String(value).toLowerCase().includes(term);
                });
            });
        });
    };

    // Apply advanced filters from the filter menu
    const applyAdvancedFilters = (data, filters) => {
        if (!filters || filters.length === 0) return data;

        return data.filter((item) => {
            return filters.every((filter) => {
                let itemValue = item[filter.field];
                const filterValue = filter.value.toLowerCase();

                if (itemValue === undefined || itemValue === null) return false;

                if (filter.field === "Completed At") {
                    const dt = DateTime.fromFormat(itemValue, "yyyy-MM-dd HH:mm:ss");
                    itemValue = dt.isValid ? dt.toFormat("dd-MM-yyyy HH:mm:ss") : "";
                }

                const itemString = String(itemValue).toLowerCase();

                switch (filter.operator) {
                    case "contains":
                        return itemString.includes(filterValue);
                    case "Does not contain":
                        return !itemString.includes(filterValue);
                    case "equals":
                        return itemString === filterValue;
                    case "Is not":
                        return itemString !== filterValue;
                    case "startsWith":
                        return itemString.startsWith(filterValue);
                    case "endsWith":
                        return itemString.endsWith(filterValue);
                    default:
                        return true;
                }
            });
        });
    };

    const filteredData = useMemo(() => {
        if (!cicdTableData) return [];

        // Apply global search first
        let filtered = applyGlobalSearch(cicdTableData, search);
        
        // Then apply advanced filters
        filtered = applyAdvancedFilters(filtered, activeFilters);

        // Apply sorting
        let sortProperty = sortColumn;
        if (sortColumn === "completedAt") sortProperty = "Completed At";
        else if (sortColumn === "changeNumber") sortProperty = "Change Number";
        else if (sortColumn === "applicationId") sortProperty = "Application ID";
        else if (sortColumn === "applicationName") sortProperty = "Application Name";
        else if (sortColumn === "repositoryName") sortProperty = "Repository Name";
        else if (sortColumn === "environment") sortProperty = "Environment";
        else if (sortColumn === "deploymentLink") sortProperty = "Deployment Link";
        else if (sortColumn === "state") sortProperty = "State";

        if (sortProperty) {
            filtered = sortArrayByProperty(filtered, sortProperty, sortDirection);
        }

        return filtered;
    }, [cicdTableData, activeFilters, search, searchType, sortColumn, sortDirection]);

    // Client-side pagination - slice data before transformation
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

    const transformedData = useMemo(() => {
        let dataToSort = paginatedData.map((item, index) => {
            return {
                id: `row-${index}`,
                changeNumber: item["Change Number"],
                repositoryName: item["Repository Name"],
                applicationId: item["Application ID"],
                applicationName: item["Application Name"],
                environment: item["Environment"],
                state: item["State"],
                deploymentLink: item["Deployment Link"],
                completedAt: DateTime.fromFormat(item["Completed At"], 'yyyy-MM-dd HH:mm:ss').toFormat('dd-MM-yyyy HH:mm:ss'),
                _originalData: item
            };
        });

        return dataToSort;
    }, [paginatedData]);

    const handlePageChange = (event) => {
        setCurrentPage(event.detail);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    useEffect(() => {
        if (tableScrollPosition > 0) {
            const tableElement = document.querySelector('mc-table');
            if (tableElement) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }
    }, [transformedData]);

    useEffect(() => {
        setTableStyles(`
            .mds-table {
                max-height: 65vh !important;
            }
        `);
    }, [transformedData]);

    useEffect(() => {
        const now = DateTime.now();
        const start = DateTime.fromJSDate(selectedTime.start);
        const end = DateTime.fromJSDate(selectedTime.end);

        const isPast = start.year < now.year ||
            (start.year === now.year && start.month < now.month) ||
            (start.year === now.year && start.month === now.month && start.day < now.day) ||
            (start.year === now.year && start.month === now.month && start.day === now.day && start.hour < now.hour) ||
            (start.year === now.year && start.month === now.month && start.day === now.day && start.hour === now.hour && start.minute < now.minute);

        const isFuture = end.year > now.year ||
            (end.year === now.year && end.month > now.month) ||
            (end.year === now.year && end.month === now.month && end.day > now.day) ||
            (end.year === now.year && end.month === now.month && end.day === now.day && end.hour > now.hour) ||
            (end.year === now.year && end.month === now.month && end.day === now.day && end.hour === now.hour && end.minute > now.minute);

        if (isPast && !isFuture) {
            setHeadingText("Past Changes");
        } else if (isFuture && !isPast) {
            setHeadingText("Future Changes");
        } else {
            setHeadingText("Ongoing Changes");
        }
    }, [selectedTime]);

    const handleChangeNumberClick = (rowData, drawerType) => {
        saveScrollPosition();

        // Use setTimeout to ensure the drawer opens after state is saved
        setTimeout(() => {
            props.setisdrawerOpen(true);
            if (drawerType === "change_details") {
                props.setDrawerCi(rowData["Configuration Item"]);
            } else {
                props.setDrawerCi(rowData["Deployment Link"]);
            }
            props.setDrawerCiCategory(rowData["Category"]);
            props.setDrawerChange(rowData["Change Number"]);
            props.setDrawerType(drawerType);
        }, 0);
    };

    const getStateColor = (state) => {
        switch (state.toLowerCase()) {
            case 'success':
                return '#00786b';
            case 'failure':
            case 'failed':
                return '#e75858';
            case 'in_progress':
            case 'running':
                return '#0077b2';
            case 'cancelled':
                return '#d8d52c';
            default:
                return 'black';
        }
    };

    const handleDownloadClick = () => {
        const headers = [
            props.Header1 || "Change Number",
            props.Header2 || "Application ID",
            props.Header3 || "Application Name",
            props.Header4 || "Repository Name",
            props.Header5 || "Completed At",
            props.Header6 || "Environment",
            props.Header7 || "Deployment Link",
            props.Header8 || "State",
        ];
        const csvData = filteredData.map(item => [
            item["Change Number"],
            item["Application ID"],
            item["Application Name"],
            item["Repository Name"],
            item["Completed At"],
            item["Environment"],
            item["Deployment Link"],
            item["State"]
        ]);
        const csv = Papa.unparse({
            fields: headers,
            data: csvData,
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const fileName = `CICD_Changes_${DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss')}.csv`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    const handleFilterApply = (filters) => {
        setActiveFilters(filters);
    };

    const handleTableSorted = ({ detail: { column, direction } }) => {
        setSortColumn(column);
        setSortDirection(direction);

        // Preserve scroll position after sorting
        setTimeout(() => {
            const tableElement = document.querySelector('mc-table');
            if (tableElement && tableScrollPosition > 0) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }, 100);
    }

    // Save scroll position before interactions
    const saveScrollPosition = () => {
        const tableElement = document.querySelector('mc-table');
        if (tableElement) {
            const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                tableElement.querySelector('.table-container');
            if (scrollContainer) {
                setTableScrollPosition(scrollContainer.scrollTop);
            }
        }
    };

    const handleDeploymentLinkClick = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="home-table-container" style={{ marginTop: "10px" }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    position: 'relative',
                }}
            >
                <div style={{ flex: '0 0 auto' }}>
                    <FilterButton
                        columns={filterColumns}
                        data={cicdTableData || []}
                        onApply={handleFilterApply}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                    />
                </div>
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        width: 'max-content',
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ fontWeight: "600", fontSize: '1.2rem', color: "#555" }}>
                        {headingText}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: '0 0 auto' }}>
                    {!props.isLoading &&
                        <span style={{ fontSize: '1rem', color: '#0077b2' }}>
                            Total: {filteredData.length}
                        </span>
                    }
                    <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                    >
                        <span slot="trailingicon">
                            <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                    </McButton>
                </div>
            </div>
            <div>
                <McTable
                    data={transformedData}
                    columns={tableColumns}
                    datakey="id"
                    headersticky
                    fit="small"
                    sortmanual
                    sortchange={handleTableSorted}
                    sortdefaultcolumnid={sortColumn}
                    sortdefaultdirection={sortDirection}
                    customstyles={tableStyles}
                    resetscrollonpagechange={false}
                    stateslotheight="200px"
                >
                    {props.isLoading ? (
                        <mc-loading-indicator slot="state"></mc-loading-indicator>
                    ) : (
                        transformedData?.length === 0 && (
                            <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600" }}>
                                No Data Found
                            </div>
                        )
                    )}
                    {transformedData?.map((row) => (
                        <React.Fragment key={row.id}>
                            {/* Change Number Cell */}
                            <div slot={`${row.id}_changeNumber`}>
                                <span
                                    onClick={() => handleChangeNumberClick(row._originalData, "deployment_details")}
                                    style={{
                                        color: "#0077b2",
                                        textDecoration: "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    {row.changeNumber}
                                </span>
                            </div>

                            {/* Application Name Cell with Relation */}
                            <div slot={`${row.id}_applicationName`}>
                                <div>
                                    <span>{row.applicationName}</span>
                                    {row._originalData["Relation"] && (
                                        <div style={{ fontSize: '0.75rem', color: '#666666' }}>
                                            {row._originalData["Relation"]}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Environment Cell */}
                            <div slot={`${row.id}_environment`}>
                                <span style={{
                                    backgroundColor: '#f0f0f0',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem'
                                }}>
                                    {row.environment}
                                </span>
                            </div>

                            {/* State Cell */}
                            <div slot={`${row.id}_state`}>
                                <span style={{
                                    color: getStateColor(row.state),
                                    fontWeight: "500"
                                }}>
                                    {row.state}
                                </span>
                            </div>

                            {/* Deployment Link Cell */}
                            <div slot={`${row.id}_deploymentLink`}>
                                {row.deploymentLink && (
                                    <Tooltip title="Open deployment link">
                                        <IconButton
                                            onClick={() => handleDeploymentLinkClick(row.deploymentLink)}
                                            style={{
                                                color: '#0077b2',
                                                padding: '4px'
                                            }}
                                            size="small"
                                        >
                                            <LaunchIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </React.Fragment>
                    ))}
                </McTable>

                {/* Pagination Component */}
                {!props.isLoading && filteredData.length > recordsPerPage && (
                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <McPagination
                            currentpage={currentPage}
                            totalpages={totalPages}
                            pagechange={handlePageChange}
                        />
                    </div>
                )}
            </div>
            <div style={{ marginTop: "8vh", visibility: "hidden" }}>
                ""
            </div>
        </div>
    );
}
